import { clearWebsiteThreadIds, getWebsiteThreadIds } from "src/utils/websiteThreadCookie";

const THREAD_IDS_KEY = "thread_ids";
const URL_THREAD_ID_PARAM = "thread_id";

/**
 * Stores written by older bundles or by the marketing site. They are only ever
 * read to be drained into `thread_ids` — never written.
 */
const LEGACY_THREAD_ID_KEY = "thread_id";

/**
 * Most anonymous chat IDs we keep, and the most we will ever ask the backend
 * to map in one request.
 *
 * Must not exceed the server's own bound (MAX_ANONYMOUS_THREADS_PER_REQUEST in
 * serializers/threads.py, also 50) — over it the endpoint answers 400, and
 * because a 400 is deterministic the client would retry the same oversized
 * batch on every login and never map anything.
 */
export const MAX_ANONYMOUS_THREAD_IDS = 50;

/**
 * localStorage `thread_ids` is the one store for anonymous thread IDs. It is
 * the only key this module writes.
 *
 * The history is append-only and ordered oldest-first, so index 0 is the
 * oldest chat and the last entry is the newest. Past MAX_ANONYMOUS_THREAD_IDS
 * the oldest are dropped from the front: someone with more than 50 anonymous
 * chats keeps their 50 most recent.
 */

const normalize = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const cleaned = value.replace(/"/g, "").trim();
  if (!cleaned || cleaned === "null") {
    return null;
  }
  return cleaned;
};

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const expireCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

const readUrlThreadId = (): string | null => {
  const { pathname, search } = window.location;
  if (pathname.startsWith("/chat/") || pathname.startsWith("/document-library")) {
    return null;
  }
  return new URLSearchParams(search).get(URL_THREAD_ID_PARAM);
};

/**
 * Never throws. A corrupt key, a non-array value, or storage being unavailable
 * (private mode, blocked site data) all degrade to "no history" rather than
 * breaking thread creation for the caller.
 */
const readStoredThreadIds = (): string[] => {
  try {
    const raw = localStorage.getItem(THREAD_IDS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((entry) => normalize(typeof entry === "string" ? entry : null)).filter(Boolean) as string[];
  } catch {
    return [];
  }
};

/** Keep the newest `MAX_ANONYMOUS_THREAD_IDS`, discarding from the front. */
const capOldestFirst = (threadIds: string[]): string[] =>
  threadIds.length <= MAX_ANONYMOUS_THREAD_IDS ? threadIds : threadIds.slice(-MAX_ANONYMOUS_THREAD_IDS);

/**
 * Returns false when storage rejected the write (quota exceeded, blocked), so
 * the migration knows not to delete the stores it was copying from.
 */
const writeStoredThreadIds = (threadIds: string[]): boolean => {
  try {
    if (threadIds.length === 0) {
      localStorage.removeItem(THREAD_IDS_KEY);
    } else {
      localStorage.setItem(THREAD_IDS_KEY, JSON.stringify(threadIds));
    }
    return true;
  } catch {
    // Losing history is strictly better than breaking chat creation.
    return false;
  }
};

/** Append IDs not already present, keeping existing positions. */
const appendUnique = (stored: string[], incoming: (string | null)[]): string[] => {
  const merged = [...stored];
  incoming.forEach((threadId) => {
    if (threadId && !merged.includes(threadId)) {
      merged.push(threadId);
    }
  });
  return merged;
};

/**
 * Move every ID held by the legacy stores into `thread_ids`, then clear them.
 *
 * Sources: the domain-wide `website_thread_id` cookie (written by the
 * marketing site), the 1-day `thread_id` cookie and the localStorage
 * `thread_id` key (both written by bundles before `thread_ids` became the only
 * store). The legacy single values were the most recent thread, so they are
 * appended last.
 *
 * The sources are cleared only once the `thread_ids` write succeeds — with
 * storage blocked they are the only copy, and the next call retries.
 *
 * Idempotent and cheap; called from `resolveAnonymousThreadIds` so a cookie the
 * marketing site sets mid-session is still picked up before mapping.
 */
export const migrateLegacyThreadStores = (): void => {
  let legacyLocalValue: string | null = null;
  try {
    legacyLocalValue = localStorage.getItem(LEGACY_THREAD_ID_KEY);
  } catch {
    // storage unavailable — nothing to read, nothing to write
  }
  const websiteIds = getWebsiteThreadIds();
  const legacyCookieValue = readCookie(LEGACY_THREAD_ID_KEY);

  if (websiteIds.length === 0 && legacyCookieValue === null && legacyLocalValue === null) {
    return;
  }

  const stored = readStoredThreadIds();
  const merged = appendUnique(stored, [
    ...websiteIds.map((id) => normalize(id)),
    normalize(legacyCookieValue),
    normalize(legacyLocalValue),
  ]);

  if (merged.length !== stored.length && !writeStoredThreadIds(capOldestFirst(merged))) {
    return;
  }

  if (websiteIds.length > 0) {
    clearWebsiteThreadIds();
  }
  if (legacyCookieValue !== null) {
    expireCookie(LEGACY_THREAD_ID_KEY);
  }
  if (legacyLocalValue !== null) {
    try {
      localStorage.removeItem(LEGACY_THREAD_ID_KEY);
    } catch {
      // ignore — see writeStoredThreadIds
    }
  }
};

/**
 * Append a thread ID to the end of the history, if it isn't already there,
 * dropping the oldest entries once the cap is reached.
 *
 * Dedupe is about correctness, not capacity: the mapping call must not name the
 * same thread twice, or one ID would come back in two buckets and break the
 * client's delete-by-bucket reconciliation. An ID that is already present is
 * left where it is rather than moved to the end — its position records when the
 * chat was created, which is what makes "drop the oldest" meaningful.
 */
export const rememberAnonymousThreadId = (threadId: string | null | undefined): void => {
  const normalized = normalize(threadId);
  if (!normalized) {
    return;
  }

  const stored = readStoredThreadIds();
  if (!stored.includes(normalized)) {
    writeStoredThreadIds(capOldestFirst([...stored, normalized]));
  }
};

/** The most recently created anonymous thread, or null when there is none. */
export const getLatestAnonymousThreadId = (): string | null => {
  migrateLegacyThreadStores();
  const stored = readStoredThreadIds();
  return stored[stored.length - 1] ?? null;
};

export interface ResolveAnonymousThreadIdArgs {
  explicitId?: string | null;
}

/**
 * Every anonymous thread ID this browser knows about, deduped, in the order
 * they should be mapped.
 *
 * Precedence: explicit argument (the thread being opened, so the backend sees
 * it first) → `thread_ids` history → URL query param (same route exclusions as
 * the original inline lookup).
 *
 * Capped at MAX_ANONYMOUS_THREAD_IDS. The explicit ID survives the trim
 * whatever its age — it is the chat being opened, and the ownership gate
 * blocks on that specific thread.
 */
export const resolveAnonymousThreadIds = ({ explicitId }: ResolveAnonymousThreadIdArgs = {}): string[] => {
  migrateLegacyThreadStores();

  const explicit = normalize(explicitId);
  const candidates = [explicit, ...readStoredThreadIds(), normalize(readUrlThreadId())];

  const seen = new Set<string>();
  const resolved: string[] = [];
  candidates.forEach((candidate) => {
    if (candidate && !seen.has(candidate)) {
      seen.add(candidate);
      resolved.push(candidate);
    }
  });

  if (resolved.length <= MAX_ANONYMOUS_THREAD_IDS) {
    return resolved;
  }
  if (!explicit) {
    return capOldestFirst(resolved);
  }
  // Pin the opened chat at the front and drop the oldest of the remainder.
  return [explicit, ...resolved.slice(1).slice(-(MAX_ANONYMOUS_THREAD_IDS - 1))];
};

/**
 * Single-value shim. Callers that need "the one thread being opened" (the
 * ownership gate, PostAuth) rather than the whole history.
 */
export const resolveAnonymousThreadId = (args: ResolveAnonymousThreadIdArgs = {}): string | null =>
  resolveAnonymousThreadIds(args)[0] ?? null;

/**
 * Drop specific IDs from the history.
 *
 * Called with exactly the IDs the backend named in its response — all four
 * buckets are terminal, so anything it reported is done with. IDs the server
 * never mentioned (network failure, 5xx) stay behind and retry on next login.
 */
export const forgetAnonymousThreadIds = (threadIds: string[]): void => {
  const doomed = new Set(threadIds.map((id) => normalize(id)).filter(Boolean) as string[]);
  if (doomed.size === 0) {
    return;
  }
  writeStoredThreadIds(readStoredThreadIds().filter((id) => !doomed.has(id)));
};

/**
 * Clears the history outright, legacy stores included. Retained for the error
 * paths that want a hard reset rather than a reconciliation against a server
 * response.
 */
export const clearAnonymousThreadId = (): void => {
  writeStoredThreadIds([]);
  try {
    localStorage.removeItem(LEGACY_THREAD_ID_KEY);
  } catch {
    // ignore — see writeStoredThreadIds
  }
  expireCookie(LEGACY_THREAD_ID_KEY);
  clearWebsiteThreadIds();
};
